
import { useState, useEffect } from 'react';
import { formatInAlbania } from '@/lib/date-utils';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { DateRange } from 'react-day-picker';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, formatDistanceStrict } from 'date-fns';
import { cn } from '@/lib/utils';
import { getCheckInOutRecords } from '@/api/dashboard';
import { useQuery } from '@tanstack/react-query';
import { useGym } from '@/contexts/gym-context';

interface CheckInTableProps {
  refreshKey?: number;
}

export function CheckInTable({ refreshKey }: CheckInTableProps) {
  const { t } = useTranslation();
  const { selectedGym } = useGym();
  const [dateRange, setDateRange] = useState<{
    from: Date;
    to: Date;
  }>({
    from: new Date(),
    to: new Date(),
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Format dates for API call (DD-MM-YYYY)
  const formatDateForAPI = (date: Date) => {
    return format(date, 'dd-MM-yyyy');
  };

  const {
    data: checkInOutRecords = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      'check-in-out-records',
      formatDateForAPI(dateRange.from),
      formatDateForAPI(dateRange.to),
      selectedGym,
    ],
    queryFn: () =>
      getCheckInOutRecords(
        formatDateForAPI(dateRange.from),
        formatDateForAPI(dateRange.to),
        selectedGym === 'all' ? undefined : selectedGym
      ),
    enabled: !!dateRange.from && !!dateRange.to,
  });

  // Refetch when refreshKey or selectedGym changes
  useEffect(() => {
    if (refreshKey !== undefined || selectedGym) {
      refetch();
    }
  }, [refreshKey, selectedGym, refetch]);

  // Sort records by check-in time (most recent first)
  const sortedRecords = [...checkInOutRecords].sort(
    (a, b) => new Date(b.checkIn).getTime() - new Date(a.checkIn).getTime()
  );

  // Pagination calculations
  const totalItems = sortedRecords.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRecords = sortedRecords.slice(startIndex, endIndex);

  // Reset to first page when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [checkInOutRecords, itemsPerPage]);

  // Calculate duration between check-in and check-out
  const calculateDuration = (checkIn: string, checkOut: string | null) => {
    if (!checkOut) return '-';
    return formatDistanceStrict(new Date(checkOut), new Date(checkIn));
  };

  const handleDateRangeChange = (range: DateRange | undefined) => {
    if (!range) return;

    if (range.from && range.to) {
      setDateRange({
        from: range.from,
        to: range.to,
      });
    } else if (range.from) {
      setDateRange({
        from: range.from,
        to: range.from,
      });
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number.parseInt(value));
    setCurrentPage(1);
  };

  return (
    <div className="space-y-4">
      {/* Date Range Picker and Items Per Page */}
      <div className="flex items-center justify-between gap-4">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                'w-[300px] justify-start text-left font-normal',
                !dateRange && 'text-muted-foreground'
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {dateRange?.from ? (
                dateRange.to ? (
                  <>
                    {format(dateRange.from, 'LLL dd, y')} -{' '}
                    {format(dateRange.to, 'LLL dd, y')}
                  </>
                ) : (
                  format(dateRange.from, 'LLL dd, y')
                )
              ) : (
                <span>Pick a date range</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={dateRange?.from}
              selected={dateRange}
              onSelect={handleDateRangeChange}
              numberOfMonths={2}
            />
          </PopoverContent>
        </Popover>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Items per page:</span>
          <Select
            value={itemsPerPage.toString()}
            onValueChange={handleItemsPerPageChange}
          >
            <SelectTrigger className="w-[80px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5</SelectItem>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="text-center py-4 text-muted-foreground">Loading...</div>
      ) : sortedRecords.length === 0 ? (
        <div className="text-center py-4 text-muted-foreground">
          {selectedGym && selectedGym !== 'all'
            ? t(
                'checkIn.noCheckInsForGym',
                'No check-ins found for the selected gym in this date range'
              )
            : t('checkIn.noCheckIns')}
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('checkIn.user')}</TableHead>
                <TableHead>{t('checkIn.gym')}</TableHead>
                <TableHead>{t('checkIn.checkInTime')}</TableHead>
                <TableHead>{t('checkIn.checkOutTime')}</TableHead>
                <TableHead>{t('checkIn.duration')}</TableHead>
                <TableHead>{t('checkIn.status')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedRecords.map((record) => (
                <TableRow key={record._id}>
                  <TableCell className="font-medium">
                    {record.userId?.name || 'Unknown'} {record.userId?.surname || 'User'}
                  </TableCell>
                  <TableCell>{record.gymId.name}</TableCell>
                  <TableCell>
                    {formatInAlbania(record.checkIn, 'DD-MM-YYYY HH:mm')}
                  </TableCell>
                  <TableCell>
                    {record.checkOut
                      ? formatInAlbania(record.checkOut, 'DD-MM-YYYY HH:mm')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {calculateDuration(record.checkIn, record.checkOut)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={record.checkOut ? 'secondary' : 'default'}>
                      {record.checkOut
                        ? t('checkIn.completed')
                        : t('checkIn.active')}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of{' '}
                {totalItems} entries
                {selectedGym && selectedGym !== 'all' && ' (filtered by gym)'}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNumber: number;
                    if (totalPages <= 5) {
                      pageNumber = i + 1;
                    } else if (currentPage <= 3) {
                      pageNumber = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNumber = totalPages - 4 + i;
                    } else {
                      pageNumber = currentPage - 2 + i;
                    }

                    return (
                      <Button
                        key={pageNumber}
                        variant={
                          currentPage === pageNumber ? 'default' : 'outline'
                        }
                        size="sm"
                        onClick={() => handlePageChange(pageNumber)}
                        className="w-8 h-8 p-0"
                      >
                        {pageNumber}
                      </Button>
                    );
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
